import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { AttachmentType, ClockService, Complaint, ComplaintAppointment, DocumentAttachment } from '@icc/b2b/shared';
import { core } from '@icc/shared';
import { ComplaintService } from '../complaint.service';
import { ComplaintAppointmentService } from '../complaint-appointment.service';
import { ToastService } from '@icc/shared/helpers';
import { TranslateService } from '@ngx-translate/core';

interface ExtendedDocumentAttachment extends DocumentAttachment {
    preview: string;
}

@Component({
    selector: 'icc-complaint-appointment-protocol-edit',
    templateUrl: './complaint-appointment-protocol-edit.component.html',
    styleUrls: ['./complaint-appointment-protocol-edit.component.scss'],
})
export class ComplaintAppointmentProtocolEditComponent implements OnInit {
    @Input() complaintAppointment!: ComplaintAppointment;
    @Input() complaint!: Complaint;
    @Input() editMode!: boolean;
    @Input() complaintAppointmentAttachments: DocumentAttachment[] = [];
    @Input() clientView!: boolean;
    @Input() clientSigned!: boolean;

    @Output() addAttachmentToAppointment = new EventEmitter<void>();
    @Output() removeAttachmentToAppointment = new EventEmitter<void>();

    currentDate = new Date();

    additionalProtocolFiles: ExtendedDocumentAttachment[] = [];

    workFinished = false;

    servicemanView = true;

    additionalProtocolFileError: string | null = null;

    constructor(
        private complaintService: ComplaintService,
        private complaintAppointmentService: ComplaintAppointmentService,
        private toastService: ToastService,
        private translateService: TranslateService
    ) {}

    ngOnInit(): void {
        if (this.complaintAppointment.servicemanSignatureImage) {
            this.additionalProtocolFiles = this.getAttachments('additionalProtocolFile').map(attachment => ({
                ...attachment,
                preview: '/complaint_attachments/' + attachment.uuid,
            }));
        } else {
            this.loadAttachments();
        }
    }

    async onAddAttachment(files: FileList, type: DocumentAttachment['attachmentType']) {
        const maxSizeInMB = 7;
        const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
        const maxFiles = 20;

        this.additionalProtocolFileError = null;

        if (files[0]) {
            if (files[0].size > maxSizeInBytes) {
                this.additionalProtocolFileError = this.translateService.instant('COMPLAINT|Plik jest zbyt duży. Maksymalna wielkość to {maxSizeInMB}MB.', { maxSizeInMB });
                (document.getElementById('additionalProtocolFile' + this.complaintAppointment.uuid) as HTMLInputElement).value = '';
                return;
            }

            if (type === 'additionalProtocolFile' && this.additionalProtocolFiles.length >= maxFiles) {
                this.additionalProtocolFileError = this.translateService.instant('COMPLAINT|Osiągnięto limit zdjęć. Możesz dodać maksymalnie {maxFiles} zdjęć.', { maxFiles });
                (document.getElementById('additionalProtocolFile' + this.complaintAppointment.uuid) as HTMLInputElement).value = '';
                return;
            }

            const reader = new FileReader();
            const data = await core.blobToArrayBuffer(files[0]);
            const newAttachment: DocumentAttachment = {
                uuid: core.generateUUID(),
                type: 'complaint',
                documentId: this.complaint?.uuid,
                appointmentId: this.complaintAppointment?.uuid,
                fileType: files[0].type,
                created: new Date(),
                modified: new Date(),
                machineId: ClockService.getClock().timestamp.node(),
                name: files[0].name,
                data,
                attachmentType: type,
                description: '',
            };
            await this.complaintService.cacheAttachment(newAttachment.uuid, files[0]);
            this.complaintAppointmentService.addAttachment(this.complaintAppointment.uuid, newAttachment);
            reader.onload = (event: any) => {
                if (type === 'additionalProtocolFile') {
                    const extendedAttachment: ExtendedDocumentAttachment = {
                        ...newAttachment,
                        preview: event.target.result,
                    };
                    this.complaintAppointmentService.addAdditionalProtocolFile(this.complaintAppointment.uuid, extendedAttachment);
                    this.loadAttachments();
                }
            };
            reader.readAsDataURL(files[0]);
            this.addAttachmentToAppointment.emit();
        }
    }

    getAttachments(type: AttachmentType): DocumentAttachment[] {
        return this.complaintAppointmentAttachments.filter(
            (attachment) => attachment.attachmentType === type
        );
    }

    getAttachment(type: DocumentAttachment['attachmentType'], uuid?: string) {
        const attachment = this.complaintAppointmentAttachments.find(
            (el) => el.attachmentType === type && (!uuid || el.uuid === uuid)
        );
        return attachment;
    }

    removeAttachment(type: DocumentAttachment['attachmentType'], uuid?: string) {
        if (type === 'additionalProtocolFile') {
            this.complaintAppointmentService.removeAdditionalProtocolFile(this.complaintAppointment.uuid, uuid);
            this.complaintAppointmentService.removeAttachment(this.complaintAppointment.uuid, uuid);
            this.removeAttachmentToAppointment.emit();
        }
        this.loadAttachments();
    }

    getAttachmentImg(uuid: string) {
        const attachment = this.additionalProtocolFiles.find(
            (el) =>
                el &&
                el.uuid === uuid
        );
        if (attachment) {
            if (this.complaintService.cachedAttachments.has(attachment.uuid)) {
                return this.complaintService.cachedAttachments.get(attachment.uuid);
            }

            return '/complaint_attachments/' + attachment.uuid;
        }
        return '';
    }

    loadAttachments(): void {
        const appointmentUuid = this.complaintAppointment.uuid;

        this.complaintAppointmentAttachments = [];
        this.additionalProtocolFiles = [];

        this.complaintAppointmentAttachments = this.complaintAppointmentService.getAttachments(appointmentUuid);
        this.additionalProtocolFiles = this.complaintAppointmentService.getAdditionalProtocolFiles(appointmentUuid);
    }
}
